import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { CURRENCY_CODES, CURRENCY_SYMBOLS } from "@/lib/format";

/**
 * Compact segmented control for USD / EUR / NOK plus an "Approx · ECB · date" tag
 * that reveals the live rates on hover.
 */
export default function CurrencyToggle({ value, onChange, rates, asOf, source, formatDate, testPrefix = "" }) {
  return (
    <div className="flex items-center gap-3">
      <div
        data-testid={`${testPrefix}currency-toggle`}
        role="tablist"
        className="inline-flex bg-slate-100 rounded-sm p-0.5 border border-slate-200"
      >
        {CURRENCY_CODES.map((code) => {
          const active = value === code;
          return (
            <button
              key={code}
              role="tab"
              aria-selected={active}
              data-testid={`${testPrefix}currency-${code}`}
              onClick={() => onChange(code)}
              className={`px-3 py-1.5 text-xs font-medium tracking-wide transition-colors rounded-sm ${
                active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {CURRENCY_SYMBOLS[code]} {code}
            </button>
          );
        })}
      </div>

      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              data-testid={`${testPrefix}fx-info`}
              className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-800 transition-colors"
            >
              <Info className="h-3 w-3" />
              Approx · ECB · {formatDate(asOf) || "—"}
            </button>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs bg-slate-800 text-white text-xs border-0">
            <div className="font-semibold mb-1">{source || "European Central Bank"}</div>
            <div className="mono text-slate-200">
              1 EUR = {rates?.USD?.toFixed(4)} USD<br />
              1 EUR = {rates?.NOK?.toFixed(4)} NOK
            </div>
            <div className="mt-2 text-slate-400">Converted values are approximate.</div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
